// categoriasProducto.js
// FUENTE ÚNICA DE VERDAD para las categorías de productos en Limpack.
//
// Antes (29-may-2026) la lista estaba duplicada en 3 lugares (Stock.jsx,
// Producto.jsx, Tienda.jsx) y se desincronizaban silenciosamente — productos
// nuevos en categoría 'manos' no aparecían en el filtro de Stock porque su
// dropdown no la incluía.
//
// Cómo agregar / sacar una categoría:
//   1. Editar SOLO este archivo.
//   2. Los 3 consumers la levantan automáticamente.
//   3. Si la categoría tiene productos en BD, asegurarse de que `enTienda: true`.
//
// Estructura de cada categoría:
//   id            string  → valor que se guarda en productos.categoria (BD)
//   labelCorto    string  → para chips/dropdowns compactos (tienda)
//   labelLargo    string  → para dropdowns con ayuda contextual (admin)
//   icono         string  → emoji default (usado en admin)
//   iconoTienda   string  → emoji para la tienda pública (puede diferir)
//   prefijoSku    string  → para sugerir el próximo SKU al cargar producto
//   enTienda      bool    → si aparece como filtro en la tienda pública
//                           (false para 'otros' que es catch-all interno)

export const CATEGORIAS_PRODUCTO = [
  { id: 'multiuso',       labelCorto: 'Multiuso',        labelLargo: 'Multiuso (limpiadores de superficies, todo uso)',                icono: '🌟', iconoTienda: '✨',  prefijoSku: 'MUL', enTienda: true },
  { id: 'lavarropa',      labelCorto: 'Para la ropa',    labelLargo: 'Ropa (detergente, suavizante)',                                  icono: '🧺', iconoTienda: '👕',  prefijoSku: 'L',   enTienda: true },
  { id: 'lavandinas',     labelCorto: 'Lavandina',       labelLargo: 'Lavandinas (cloro / agua sanitaria — Brilha, Gota Limpa, Suprema)', icono: '🧪', iconoTienda: '💧',  prefijoSku: 'LAV', enTienda: true },
  { id: 'desinfectantes', labelCorto: 'Desinfectantes',  labelLargo: 'Desinfectantes (Cottonmax floral / lavanda, etc.)',              icono: '🧫', iconoTienda: '🧫',  prefijoSku: 'DES', enTienda: true },
  { id: 'ambientadores',  labelCorto: 'Ambientadores',   labelLargo: 'Ambientadores (home spray, aerosol perfumante, difusores)',      icono: '🌸', iconoTienda: '🌸',  prefijoSku: 'AMB', enTienda: true },
  { id: 'cocina',         labelCorto: 'Cocina',          labelLargo: 'Cocina (lavavajillas, desengrasante)',                           icono: '🧴', iconoTienda: '🍳',  prefijoSku: 'COC', enTienda: true },
  { id: 'bano',           labelCorto: 'Baño',            labelLargo: 'Baño (limpiador específico, antical, desinfectante WC)',         icono: '🛁', iconoTienda: '🛁',  prefijoSku: 'BAN', enTienda: true },
  { id: 'papel',          labelCorto: 'Papel',           labelLargo: 'Papel (higiénico, cocina, servilletas)',                         icono: '🧻', iconoTienda: '🧻',  prefijoSku: 'PAP', enTienda: true },
  { id: 'panuelos',       labelCorto: 'Pañuelos',        labelLargo: 'Pañuelos descartables (packs de bolso, tissues)',                icono: '🤧', iconoTienda: '🤧',  prefijoSku: 'PAN', enTienda: true },
  { id: 'manos',          labelCorto: 'Manos',           labelLargo: 'Manos (jabón líquido, espuma, glicerina — Ecume)',               icono: '🧴', iconoTienda: '🧴',  prefijoSku: 'MAN', enTienda: true },
  { id: 'cuidado_personal', labelCorto: 'Cuidado personal', labelLargo: 'Cuidado personal (jabones, shampoo, acondicionador, crema, desodorante)', icono: '🧼', iconoTienda: '🧼', prefijoSku: 'CP',  enTienda: true },
  { id: 'higiene',        labelCorto: 'Higiene',         labelLargo: 'Higiene (alcohol, gel, desinfectante manos líquido)',            icono: '🧴', iconoTienda: '🧴',  prefijoSku: 'HIG', enTienda: true },
  { id: 'bolsas',         labelCorto: 'Bolsas',          labelLargo: 'Bolsas (residuos, freezer, supermercado)',                       icono: '🛍️', iconoTienda: '🗑️',  prefijoSku: 'BOL', enTienda: true },
  { id: 'accesorios',     labelCorto: 'Accesorios',      labelLargo: 'Accesorios (esponjas, trapos, escobas)',                         icono: '🧽', iconoTienda: '🧽',  prefijoSku: 'ACC', enTienda: true },
  { id: 'otros',          labelCorto: 'Otros',           labelLargo: 'Otros',                                                          icono: '📦', iconoTienda: '📦',  prefijoSku: 'OT',  enTienda: false },
]

/* ─────────────────────────────────────────────────────────────────────────────
 * Vistas derivadas (lo que consumen los 3 archivos)
 * ─────────────────────────────────────────────────────────────────────────── */

/** Para dropdowns de admin (Stock.jsx, Producto.jsx). Formato { v, label } */
export const CATEGORIAS_ADMIN = CATEGORIAS_PRODUCTO.map(c => ({
  v: c.id,
  label: `${c.icono} ${c.labelLargo}`,
}))

/** Para los chips de filtro de la tienda pública (Tienda.jsx). Formato { id, label, icono } */
export const CATEGORIAS_TIENDA = CATEGORIAS_PRODUCTO
  .filter(c => c.enTienda)
  .map(c => ({ id: c.id, label: c.labelCorto, icono: c.iconoTienda || c.icono }))

/** Map id → prefijo SKU (para sugerirSku). */
export const PREFIJOS_SKU_POR_CATEGORIA = Object.fromEntries(
  CATEGORIAS_PRODUCTO.map(c => [c.id, c.prefijoSku])
)

/** Helper: dado un id de categoría, devuelve el label corto. Útil para badges. */
export function labelCategoria(id) {
  const c = CATEGORIAS_PRODUCTO.find(x => x.id === id)
  return c ? c.labelCorto : id
}

/** Helper: dado un id, devuelve el icono. */
export function iconoCategoria(id, esTienda = false) {
  const c = CATEGORIAS_PRODUCTO.find(x => x.id === id)
  if (!c) return '📦'
  return esTienda ? (c.iconoTienda || c.icono) : c.icono
}
